import { Box } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import React, { FC } from "react";

import { useDataGridLocaleText } from "../../../../design/use-datagrid-locale-text";
import useTableHeight from "../../../../utils/use-table-height";
import useWorkerRequestsData from "../../controllers/use-worker-requests-data";
import { RegularShiftRequest } from "../../store/worker-requests-types";
import EmptyOverlay from "./empty-overlay";

interface FullTimeRequestsTableProps {
	openCreateNewShiftDialog: ((date: string) => void) | undefined;
	openShiftDetailsDialog: ((e: string) => void) | undefined;
	columns: any[];
	rows?: RegularShiftRequest[];
}

const FullTimeRequestsTable: FC<FullTimeRequestsTableProps> = ({
	openCreateNewShiftDialog,
	openShiftDetailsDialog,
	columns,
	rows,
}) => {
	const { boxRef, height } = useTableHeight();
	const dataGridLocaleText = useDataGridLocaleText();

	const { getAllRegularRequestsStatus, requestActionStatus } =
		useWorkerRequestsData();

	const sortedRows = rows
    ? [...rows].sort((a, b) => {
        const timeB = new Date(b?.requestDate).getTime();
        const timeA = new Date(a?.requestDate).getTime();
        
        //descending order (recent first)
        return timeB - timeA;
      })
    : [];

	const t = sortedRows?.map((request: RegularShiftRequest) => ({
		...request,
		openCreateNewShiftDialog: openCreateNewShiftDialog,
		openShiftDetailsDialog: openShiftDetailsDialog,
	}));

	return (
    <Box
      ref={boxRef}
      sx={{
        width: "100%",
        height: height || "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <DataGridPro
        rows={t ?? []}
        columns={columns}
        localeText={dataGridLocaleText}
        getRowHeight={() => "auto"}
        disableSelectionOnClick
        disableColumnSelector
        disableDensitySelector
        disableColumnMenu
        pagination
        initialState={{
          pagination: { pageSize: 25 },
        }}
        loading={
          getAllRegularRequestsStatus === "pending" ||
          requestActionStatus === "pending"
        }
        pinnedColumns={{ right: ["status", "actions"] }}
        scrollbarSize={6}
        sx={{
          flex: 1,
          minHeight: "200px",
          height: height || "100%",
          overflow: "hidden",
          border: "none !important",
          "& .MuiDataGrid-columnHeader": {
            "&.hover-column-header": {
              "& .MuiDataGrid-columnSeparator": {
                display: "none",
              },
              "& .MuiDataGrid-sortIcon": {
                display: "none",
              },
            },
            "& .MuiDataGrid-columnSeparator": {
              display: "none",
            },
          },
          "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
            py: "10px",
          },
          "& .MuiTablePagination-root": {
            marginRight: "60px",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
            height: "0px",
          },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
            outline: "none !important",
          },
          ".MuiDataGrid-cell": {
            "&:focus": {
              outline: "none",
            },
          },
          "& .actions-col-header": {
            paddingLeft: "10px !important",
            paddingRight: "10px !important",
          },
          "& .actions-col-cell": {
            paddingLeft: "10px !important",
            paddingRight: "10px !important",
          },
          ".MuiDataGrid-columnHeader:hover": {
            "& .MuiDataGrid-columnSeparator": {
              display: "flex",
            },
          },
        }}
        components={{
          NoRowsOverlay: () => <EmptyOverlay />,
        }}
      />
    </Box>
  );
};

export default FullTimeRequestsTable;
